import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import BIDNavbar from "@/components/feature/BIDNavbar";
import BIDFooter from "@/components/feature/BIDFooter";
import ServiceRequestModal from "@/components/feature/ServiceRequestModal";
import { useAuth } from "@/hooks/useAuth";
import { useDepartmentAccent } from "@/hooks/useDepartmentAccent";
import { departments } from "@/mocks/departments";
import { fetchLiveGroups, fetchLiveStats } from "@/services/zammadService";
import type { ZammadCategoryTreeNode } from "@/services/zammadService";
import { getMicrositeBannerImage } from "@/utils/images";

interface ZammadGroupNode {
  id: number;
  name: string;      // Full name: e.g. "Servicio de TI::Mesa de Ayuda::Soporte Técnico"
  label: string;     // Short name: e.g. "Soporte Técnico"
  children: ZammadGroupNode[];
}

const MICROSITE_CATEGORY_PATHS: Record<string, string[]> = {
  ti: [
    "Servicio de TI::Mesa de Ayuda",
    "Servicio de TI::CRM (HubSpot)",
    "Servicio de TI::ERP (NetSuite)",
    "Servicio de TI::Data Lake",
    "Servicio de TI::BPM",
    "Servicio de TI::Solicitudes en Sitio",
    "Servicio de TI::Seguridad de la información",
    "Servicio de TI::SharePoint BID Tecnología",
    "Servicio de TI::Centro de Excelencia",
    "Servicio de TI::MSBID",
    "Servicio de TI::Tu parque Vivo",
    "Servicio de TI::Presupuestos BID",
    "Servicio de TI::Servicio pagos con tarjeta de crédito BID",
  ],
  administracion: ["Administración"],
  contraloria: ["Contraloría"],
  "diseno-marketing": ["Diseño y Marketing"],
  "bid-tecnologia": [
    "Servicio de TI::SharePoint BID Tecnología",
    "Servicio de TI::Centro de Excelencia",
    "Servicio de TI::Servicio Nómina-SEIEM",
  ],
  "servicios-generales": [
    "Administración::Mantenimiento a instalaciones",
    "Administración::Pedido de papelería especial",
    "Administración::Mensajería",
    "Administración::Alta de proveedores para acceso a montacargas",
    "Administración::Reporte por robo o extravió de tarjeta de acceso al edificio",
  ],
};

function normalizeCategoryPath(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s*::\s*/g, "::")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function getCategorySlug(name: string): string {
  const normalized = normalizeCategoryPath(name);
  if (normalized.includes("servicio de ti") || normalized === "ti") return "ti";
  if (normalized === "administracion") return "administracion";
  if (normalized.includes("servicios generales")) return "servicios-generales";
  if (normalized.includes("talento humano")) return "talento-humano";
  if (normalized.includes("legal")) return "legal";
  if (normalized.includes("contraloria")) return "contraloria";
  if (normalized.includes("diseno") || normalized.includes("marketing")) return "diseno-marketing";

  return normalized
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function findCategoryNodesByPaths(
  nodes: ZammadCategoryTreeNode[],
  paths: string[]
): ZammadCategoryTreeNode[] {
  const requestedPaths = new Set(paths.map(normalizeCategoryPath));
  const matches: ZammadCategoryTreeNode[] = [];

  function visit(currentNodes: ZammadCategoryTreeNode[]) {
    for (const node of currentNodes) {
      const nodePath = normalizeCategoryPath(node.value || node.name);
      if (requestedPaths.has(nodePath)) matches.push(node);
      if (node.children?.length) visit(node.children);
    }
  }

  visit(nodes);
  return matches;
}



// Map manual frequent tickets to target Zammad group names
const FAVORITES_BY_DEPARTMENT: Record<string, { label: string; groupName: string; parentPath: string }[]> = {
  ti: [
    {
      label: "Solicitar laptop",
      groupName: "Servicio de TI::Mesa de Ayuda::Hardware",
      parentPath: "Mesa de Ayuda › Hardware"
    },
    {
      label: "No puedo imprimir",
      groupName: "Servicio de TI::Mesa de Ayuda::Soporte Técnico",
      parentPath: "Mesa de Ayuda › Soporte Técnico"
    },
    {
      label: "Cambio de contraseña",
      groupName: "Servicio de TI::Mesa de Ayuda::Accesos y Cuentas",
      parentPath: "Mesa de Ayuda › Accesos y Cuentas"
    },
    {
      label: "Acceso a ERP (NetSuite)",
      groupName: "Servicio de TI::ERP (NetSuite)::Roles y Accesos",
      parentPath: "ERP (NetSuite) › Roles y Accesos"
    },
    {
      label: "Solicitud de equipo",
      groupName: "Servicio de TI::Solicitudes en Sitio::Equipos",
      parentPath: "Solicitudes en Sitio › Equipos"
    },
    {
      label: "Acceso físico (edificio)",
      groupName: "Servicio de TI::Solicitudes en Sitio::Acceso Físico",
      parentPath: "Solicitudes en Sitio › Acceso Físico"
    },
    {
      label: "Automatización de proceso",
      groupName: "Servicio de TI::BPM::Automatización",
      parentPath: "BPM › Automatización"
    }
  ],
  administracion: [
    {
      label: "Préstamo de automóvil",
      groupName: "Administración::Administración de automóviles::Préstamo del auto solicitudes y cancelaciones",
      parentPath: "Administración de automóviles"
    },
    {
      label: "Seguimiento a vuelos",
      groupName: "Administración::Seguimiento a vuelos (solicitudes, cambios y cancelaciones)",
      parentPath: "Viajes y Vuelos"
    },
    {
      label: "Viáticos y Reembolsos",
      groupName: "Administración::Seguimiento a autorización de viáticos y reembolsos (área administrativa)",
      parentPath: "Finanzas"
    },
    {
      label: "Reservar estacionamiento",
      groupName: "Administración::Reservar un lugar de estacionamiento",
      parentPath: "Instalaciones"
    }
  ],
  "servicios-generales": [
    {
      label: "Mantenimiento a instalaciones",
      groupName: "Servicios generales::Mantenimiento a instalaciones",
      parentPath: "Mantenimiento"
    },
    {
      label: "Pedido de papelería especial",
      groupName: "Servicios generales::Pedido de papelería especial",
      parentPath: "Oficina"
    },
    {
      label: "Servicios de mensajería",
      groupName: "Servicios generales::Mensajería",
      parentPath: "Logística"
    },
    {
      label: "Robo/extravío de tarjeta",
      groupName: "Servicios generales::Reporte por robo o extravió de tarjeta de acceso al edificio",
      parentPath: "Seguridad"
    }
  ],
  "talento-humano": [
    {
      label: "Reclutamiento y selección",
      groupName: "Servicio de TI::Talento Humano::Reclutamiento",
      parentPath: "Capital Humano"
    },
    {
      label: "Tiempo y asistencia",
      groupName: "Servicio de TI::Talento Humano::Tiempo y Asistencia",
      parentPath: "Asistencia"
    },
    {
      label: "Gestión de talento",
      groupName: "Servicio de TI::Talento Humano::Gestión de Talento",
      parentPath: "Desarrollo"
    }
  ],
  "bid-tecnologia": [
    {
      label: "Nómina (Nomisystem)",
      groupName: "Servicio Nomisystem BID",
      parentPath: "Plataformas"
    },
    {
      label: "Carpetas en SharePoint",
      groupName: "SharePoint BID Tecnología",
      parentPath: "Accesos"
    },
    {
      label: "SILIC Soporte (Centro Excelencia)",
      groupName: "Centro de Excelencia::SILIC",
      parentPath: "Procesos"
    }
  ],
  contraloria: [
    {
      label: "Cuentas por cobrar",
      groupName: "Contraloría::Cuentas por cobrar",
      parentPath: "Finanzas"
    },
    {
      label: "Cuentas por pagar",
      groupName: "Contraloría::Cuentas por pagar",
      parentPath: "Finanzas"
    },
    {
      label: "Fiscal y Declaraciones",
      groupName: "Contraloría::Fiscal",
      parentPath: "Impuestos"
    }
  ],
  "diseno-marketing": [
    {
      label: "Medios digitales",
      groupName: "Diseño y Marketing::Medios digitales",
      parentPath: "Marketing"
    },
    {
      label: "Medios impresos",
      groupName: "Diseño y Marketing::Medios impresos",
      parentPath: "Publicidad"
    }
  ],
  legal: [
    {
      label: "Revisión de contratos",
      groupName: "Legal::Revisión de contratos",
      parentPath: "Corporativo"
    }
  ]
};

// Fallback common keywords for each department if Zammad has no ticket stats yet
const FALLBACK_COMMON_WORDS: Record<string, string[]> = {
  ti: ["laptop", "impresora", "contraseña", "internet", "software"],
  administracion: ["vehículo", "vuelo", "viáticos", "estacionamiento", "proveedor"],
  "servicios-generales": ["mantenimiento", "papelería", "mensajería", "acceso", "clima"],
  "talento-humano": ["vacante", "nómina", "asistencia", "expediente", "contrato"],
  "bid-tecnologia": ["SharePoint", "Nomisystem", "SILIC", "nómina", "SITE"],
  contraloria: ["factura", "pago", "SAT", "impuestos", "diario"],
  "diseno-marketing": ["diseño", "logo", "flyer", "video", "comunicado"],
  legal: ["contrato", "convenio", "poder", "firma", "auditoría"]
};

// Map keywords to specific ticket labels and Zammad groups
const KEYWORD_TO_GROUP: Record<string, { label: string; groupName: string }> = {
  // TI
  laptop: { label: "Solicitar laptop", groupName: "Servicio de TI::Mesa de Ayuda::Hardware" },
  impresora: { label: "No puedo imprimir", groupName: "Servicio de TI::Mesa de Ayuda::Soporte Técnico" },
  contraseña: { label: "Cambio de contraseña", groupName: "Servicio de TI::Mesa de Ayuda::Accesos y Cuentas" },
  internet: { label: "Internet lento/sin conexión", groupName: "Servicio de TI::Mesa de Ayuda::Soporte Técnico" },
  software: { label: "Instalar programa", groupName: "Servicio de TI::Mesa de Ayuda::Software" },
  
  // Administración
  vehículo: { label: "Préstamo de automóvil", groupName: "Administración::Administración de automóviles::Préstamo del auto solicitudes y cancelaciones" },
  vuelo: { label: "Seguimiento a vuelos", groupName: "Administración::Seguimiento a vuelos (solicitudes, cambios y cancelaciones)" },
  viáticos: { label: "Viáticos y Reembolsos", groupName: "Administración::Seguimiento a autorización de viáticos y reembolsos (área administrativa)" },
  estacionamiento: { label: "Reservar estacionamiento", groupName: "Administración::Reservar un lugar de estacionamiento" },
  proveedor: { label: "Alta/cambio de proveedor", groupName: "Administración::Aviso  alta, baja o cambios de proveedor" },
  
  // Servicios generales
  mantenimiento: { label: "Mantenimiento a instalaciones", groupName: "Servicios generales::Mantenimiento a instalaciones" },
  papelería: { label: "Pedido de papelería especial", groupName: "Servicios generales::Pedido de papelería especial" },
  mensajería: { label: "Servicios de mensajería", groupName: "Servicios generales::Mensajería" },
  acceso: { label: "Robo/extravío de tarjeta", groupName: "Servicios generales::Reporte por robo o extravió de tarjeta de acceso al edificio" },
  clima: { label: "Mantenimiento a instalaciones", groupName: "Servicios generales::Mantenimiento a instalaciones" },
  
  // Talento Humano
  vacante: { label: "Reclutamiento y selección", groupName: "Servicio de TI::Talento Humano::Reclutamiento" },
  nómina: { label: "Nómina (Nomisystem)", groupName: "Servicio Nomisystem BID" },
  asistencia: { label: "Tiempo y asistencia", groupName: "Servicio de TI::Talento Humano::Tiempo y Asistencia" },
  expediente: { label: "Gestión de talento", groupName: "Servicio de TI::Talento Humano::Gestión de Talento" },
  contrato: { label: "Gestión de talento", groupName: "Servicio de TI::Talento Humano::Gestión de Talento" },

  // BID Tecnología
  sharepoint: { label: "Carpetas en SharePoint", groupName: "SharePoint BID Tecnología" },
  nomisystem: { label: "Nómina (Nomisystem)", groupName: "Servicio Nomisystem BID" },
  silic: { label: "SILIC Soporte (Centro Excelencia)", groupName: "Centro de Excelencia::SILIC" },
  site: { label: "SILIC Soporte (Centro Excelencia)", groupName: "Centro de Excelencia::SILIC" },

  // Contraloría
  factura: { label: "Cuentas por cobrar", groupName: "Contraloría::Cuentas por cobrar" },
  pago: { label: "Cuentas por pagar", groupName: "Contraloría::Cuentas por pagar" },
  sat: { label: "Fiscal y Declaraciones", groupName: "Contraloría::Fiscal" },
  impuestos: { label: "Fiscal y Declaraciones", groupName: "Contraloría::Fiscal" },

  // Diseño y Marketing
  diseño: { label: "Medios digitales", groupName: "Diseño y Marketing::Medios digitales" },
  logo: { label: "Medios digitales", groupName: "Diseño y Marketing::Medios digitales" },
  flyer: { label: "Medios impresos", groupName: "Diseño y Marketing::Medios impresos" },
  video: { label: "Medios digitales", groupName: "Diseño y Marketing::Medios digitales" },
  comunicado: { label: "Medios digitales", groupName: "Diseño y Marketing::Medios digitales" },

  // Legal
  convenio: { label: "Revisión de contratos", groupName: "Legal::Revisión de contratos" },
  poder: { label: "Revisión de contratos", groupName: "Legal::Revisión de contratos" },
  firma: { label: "Revisión de contratos", groupName: "Legal::Revisión de contratos" }
};

// Select class icon based on the service label name
const getServiceIcon = (label: string) => {
  const lower = label.toLowerCase();
  if (lower.includes("auto") || lower.includes("car")) return "ri-car-line";
  if (lower.includes("vuelo") || lower.includes("viaje") || lower.includes("avión")) return "ri-flight-takeoff-line";
  if (lower.includes("hospedaje") || lower.includes("hotel")) return "ri-hotel-line";
  if (lower.includes("viático") || lower.includes("reembolso") || lower.includes("presupuesto")) return "ri-file-list-3-line";
  if (lower.includes("estacionamiento")) return "ri-parking-box-line";
  if (lower.includes("proveedor")) return "ri-exchange-line";
  if (lower.includes("mantenimiento") || lower.includes("clima")) return "ri-settings-3-line";
  if (lower.includes("papelería")) return "ri-file-copy-line";
  if (lower.includes("mensajería") || lower.includes("paquete")) return "ri-mail-send-line";
  if (lower.includes("tarjeta") || lower.includes("robo") || lower.includes("extravío")) return "ri-shield-line";
  if (lower.includes("reclutamiento") || lower.includes("vacante")) return "ri-user-search-line";
  if (lower.includes("nómina") || lower.includes("pago") || lower.includes("money")) return "ri-money-dollar-circle-line";
  if (lower.includes("capacitación") || lower.includes("curso")) return "ri-graduation-cap-line";
  if (lower.includes("clima laboral")) return "ri-emotion-happy-line";
  if (lower.includes("beneficio")) return "ri-heart-2-line";
  if (lower.includes("expediente")) return "ri-folder-user-line";
  if (lower.includes("soporte") || lower.includes("técnico")) return "ri-tools-line";
  if (lower.includes("acceso") || lower.includes("cuenta") || lower.includes("contraseña")) return "ri-key-2-line";
  if (lower.includes("correo") || lower.includes("comunicación")) return "ri-mail-line";
  if (lower.includes("hardware") || lower.includes("laptop") || lower.includes("equipo")) return "ri-computer-line";
  if (lower.includes("software") || lower.includes("programa") || lower.includes("licencia")) return "ri-window-line";
  return "ri-question-answer-line";
};

// Helper to normalize and split any group name by standard separators (>, ::, /)
const splitGroupParts = (fullName: string): string[] => {
  return fullName
    .split(/\s*::\s*|\s*>\s*|\s*\/\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
};

export default function DynamicDepartmentPage() {
  const { deptId } = useParams<{ deptId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, login, loginWithCredentials, logout, error: authError } = useAuth();

  // Local login states
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [credentialsError, setCredentialsError] = useState<string | null>(null);

  // Zammad groups state
  const [zammadGroups, setZammadGroups] = useState<{ id: number; name: string; active: boolean }[]>([]);
  const [permittedGroupIds, setPermittedGroupIds] = useState<number[] | null>(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  // Common ticket words state
  const [commonWords, setCommonWords] = useState<string[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalService, setModalService] = useState("");
  const [modalGroup, setModalGroup] = useState("");

  // Accordion expansion states
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [collapsedSystems, setCollapsedSystems] = useState<Set<string>>(new Set());

  const toggleCategory = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  const toggleSystemCollapse = (systemName: string) => {
    setCollapsedSystems((prev) => {
      const next = new Set(prev);
      if (next.has(systemName)) {
        next.delete(systemName);
      } else {
        next.add(systemName);
      }
      return next;
    });
  };

  // Mantener compatibilidad con enlaces guardados antes de unificar el slug.
  const currentSlug = deptId === "diseno-y-marketing" ? "diseno-marketing" : (deptId || "");



  // Set accent theme based on dept slug
  useDepartmentAccent(currentSlug);

  // Get static metadata if available
  const staticDeptInfo = useMemo(() => {
    return departments.find((d) => d.id === currentSlug);
  }, [currentSlug]);

  const [categoryObjectTree, setCategoryObjectTree] = useState<ZammadCategoryTreeNode[]>([]);

  // Fetch Zammad groups dynamically for all visitors
  // Strategy: stale-while-revalidate — show cached catalog instantly, refresh in background
  useEffect(() => {
    // 1. Restore cached groups immediately for instant UI render
    // Versionar la clave evita restaurar categorías guardadas desde otro
    // host de Zammad después de cambiar la configuración del entorno.
    const cacheKey = `zammad_categories_v2_${user?.id || "guest"}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed.groups && Array.isArray(parsed.groups)) {
          setZammadGroups(parsed.groups);
          setPermittedGroupIds(parsed.permittedGroupIds || []);
          if (parsed.categoryTree) {
            setCategoryObjectTree(parsed.categoryTree);
          }
          setLoadingGroups(false);
        }
      } catch (err) {
        console.error("Failed to parse cached Zammad groups:", err);
      }
    }

    // 2. Revalidate from Zammad in background
    let active = true;
    async function revalidate() {
      if (!cached) {
        setLoadingGroups(true);
      }
      setGroupsError(null);
      try {
        const res = await fetchLiveGroups(user ? { userId: user.id, email: user.email } : undefined);
        if (!active) return;
        if (res.error) {
          setGroupsError(res.error);
        } else {
          if (res.categoryTree && res.categoryTree.length > 0) {
            setCategoryObjectTree(res.categoryTree);
          }
          const fresh = JSON.stringify({
            groups: res.groups || [],
            permittedGroupIds: res.permittedGroupIds || [],
            categoryTree: res.categoryTree || []
          });
          // Only update state + cache if data changed
          if (fresh !== cached) {
            setZammadGroups(res.groups || []);
            setPermittedGroupIds(res.permittedGroupIds || []);
            sessionStorage.setItem(cacheKey, fresh);
          }
        }
      } catch (err) {
        if (active && !cached) {
          setGroupsError("Error al cargar los datos");
        }
      } finally {
        if (active) setLoadingGroups(false);
      }
    }
    revalidate();
    return () => {
      active = false;
    };
  }, [user]);

  const filteredActiveGroups = useMemo(() => {
    if (!zammadGroups || zammadGroups.length === 0) return [];
    return zammadGroups.filter((g) => g && g.active !== false);
  }, [zammadGroups]);

  // 2. Resolve the target parent category node for the current URL microsite slug strictly from categoryObjectTree
  const targetParentCategoryNodes = useMemo(() => {
    if (!categoryObjectTree || categoryObjectTree.length === 0) return [];

    const configuredPaths = MICROSITE_CATEGORY_PATHS[currentSlug];
    if (configuredPaths?.length) {
      return findCategoryNodesByPaths(categoryObjectTree, configuredPaths);
    }

    // Fallback para nuevos micrositios: buscar la coincidencia en todo el árbol,
    // no solamente entre las categorías raíz.
    const matchedNodes: ZammadCategoryTreeNode[] = [];
    function visit(nodes: ZammadCategoryTreeNode[]) {
      for (const node of nodes) {
        if (getCategorySlug(node.name) === currentSlug) matchedNodes.push(node);
        if (node.children?.length) visit(node.children);
      }
    }
    visit(categoryObjectTree);

    return matchedNodes;
  }, [currentSlug, categoryObjectTree]);

  // Open general modal automatically when navigated here with ?modal=general
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("modal") === "general" && targetParentCategoryNodes.length > 0) {
      setModalService("Solicitud General");
      setModalGroup(targetParentCategoryNodes[0].name);
      setModalOpen(true);
      window.history.replaceState(null, "", location.pathname);
    }
  }, [location.pathname, location.search, targetParentCategoryNodes]);

  // Use the live Zammad parent name strictly as returned by API
  const matchedZammadParentName = useMemo(() => {
    if (targetParentCategoryNodes.length === 1) {
      return targetParentCategoryNodes[0].name;
    }
    if (staticDeptInfo?.name) {
      return staticDeptInfo.name;
    }
    return currentSlug
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, [targetParentCategoryNodes, currentSlug, staticDeptInfo]);

  const deptTitle = matchedZammadParentName;
  const deptDescription = `Portal dinámico de autogestión de solicitudes y soporte técnico para ${matchedZammadParentName}.`;

  // Helper de mapeo recursivo de ZammadCategoryTreeNode a ZammadGroupNode
  const mapCategoryNodeToGroupNode = useCallback((node: ZammadCategoryTreeNode, parentPath: string): ZammadGroupNode => {
    const fullPath = node.value || (parentPath ? `${parentPath}::${node.name}` : node.name);
    return {
      id: -1,
      name: fullPath,
      label: node.name,
      children: node.children ? node.children.map((child) => mapCategoryNodeToGroupNode(child, fullPath)) : [],
    };
  }, []);

  // Parse the category tree resolved dynamically by attribute name.
  const parsedTrees = useMemo(() => {
    if (!targetParentCategoryNodes || targetParentCategoryNodes.length === 0) return [];

    const trees: { parentName: string; children: ZammadGroupNode[] }[] = [];

    for (const rootNode of targetParentCategoryNodes) {
      const isTopLevelCategory = categoryObjectTree.includes(rootNode);

      if (isTopLevelCategory && rootNode.children && rootNode.children.length > 0) {
        const childrenNodes = rootNode.children.map((child) => mapCategoryNodeToGroupNode(child, rootNode.value || rootNode.name));
        trees.push({ parentName: rootNode.name, children: childrenNodes });
      } else {
        // Los micrositios también pueden representar una rama interna o una
        // hoja. Mantener el nodo completo evita perder sus subcategorías.
        trees.push({
          parentName: staticDeptInfo?.name || rootNode.name,
          children: [mapCategoryNodeToGroupNode(rootNode, "")],
        });
      }
    }

    return trees;
  }, [targetParentCategoryNodes, categoryObjectTree, staticDeptInfo, mapCategoryNodeToGroupNode]);

  // Search state and dynamic matching lists
  const [searchQuery, setSearchQuery] = useState("");

  // Get all leaf groups under this department
  const allLeafGroups = useMemo(() => {
    const list: { id: number; name: string; label: string; parentPath: string }[] = [];
    
    function traverse(node: ZammadGroupNode, parentPath = "") {
      const path = parentPath ? `${parentPath} › ${node.label}` : node.label;
      if (node.children.length === 0) {
        list.push({
          id: node.id,
          name: node.name,
          label: node.label,
          parentPath
        });
      } else {
        for (const child of node.children) {
          traverse(child, path);
        }
      }
    }

    for (const tree of parsedTrees) {
      if (tree.children.length === 0) {
        list.push({
          id: -1,
          name: tree.parentName,
          label: tree.parentName,
          parentPath: tree.parentName
        });
      } else {
        for (const child of tree.children) {
          traverse(child, tree.parentName);
        }
      }
    }

    return list;
  }, [parsedTrees]);

  // Filter leaf groups based on search query
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return allLeafGroups.filter(
      (g) => g.label.toLowerCase().includes(q) || g.name.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [searchQuery, allLeafGroups]);

  // Check if there are any results matching the search query across all leaf groups
  const hasSearchResults = useMemo(() => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return allLeafGroups.some(
      (g) => g.label.toLowerCase().includes(q) || g.name.toLowerCase().includes(q)
    );
  }, [searchQuery, allLeafGroups]);

  // Resolve frequent tickets dynamically from live category trees for the current department
  const frequentTickets = useMemo(() => {
    return allLeafGroups.slice(0, 6).map((leaf, idx) => ({
      label: leaf.label,
      groupName: leaf.name,
      parentPath: leaf.parentPath,
      id: idx + 1,
    }));
  }, [allLeafGroups]);

  const hasParentPermission = useMemo(() => true, []);

  const handleCredentialsLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;

    setLoadingCredentials(true);
    setCredentialsError(null);
    try {
      await loginWithCredentials(username, password);
    } catch (err) {
      setCredentialsError(err instanceof Error ? err.message : "Error al iniciar sesión");
    } finally {
      setLoadingCredentials(false);
    }
  }, [username, password, loginWithCredentials]);

  const openTicketModal = (node: ZammadGroupNode) => {
    setModalService(node.label);
    setModalGroup(node.name);
    setModalOpen(true);
  };

  const handleKeywordClick = (word: string) => {
    const mapping = KEYWORD_TO_GROUP[word.toLowerCase()];
    if (mapping) {
      setModalService(mapping.label);
      setModalGroup(mapping.groupName);
      setModalOpen(true);
    } else {
      setModalService(word.charAt(0).toUpperCase() + word.slice(1));
      setModalGroup(targetParentCategoryNodes[0]?.name || "General");
      setModalOpen(true);
    }
  };

  // ──────────────────────────────────────────
  // RENDER: LOADING LOGIN OR GATE
  // ──────────────────────────────────────────
  if (authLoading) {
    return (
      <main className="relative min-h-screen bg-background-50 flex flex-col justify-between">
        <BIDNavbar />
        <div className="flex-1 flex flex-col items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin mb-4"></div>
          <p className="text-sm text-foreground-500 font-medium">Validando acceso al portal...</p>
        </div>
        <BIDFooter />
      </main>
    );
  }

  // Enforce Login Gate
  if (!user) {
    return (
      <main className="relative min-h-screen bg-background-50 flex flex-col justify-between">
        <BIDNavbar />
        <section className="flex-1 flex items-center justify-center pt-28 pb-16 px-4 md:px-6">
          <div className="w-full max-w-md bg-white rounded-2xl border border-background-200 shadow-xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-primary-50 flex items-center justify-center">
              <i className="ri-shield-user-line text-primary-500 text-3xl"></i>
            </div>
            
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-1">
              Acceso Corporativo
            </h1>
            
            <p className="text-xs text-foreground-500 mb-6 leading-relaxed">
              Ingresa tus credenciales o inicia sesión con tu cuenta corporativa.
            </p>
            
            {(credentialsError || authError) && (
              <div className="mb-5 p-3.5 rounded-xl bg-red-50 border border-red-100 text-xs text-red-600 text-left flex items-start gap-2.5">
                <i className="ri-error-warning-line text-sm mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold">Error de autenticación</p>
                  <p className="mt-0.5 text-red-500">{credentialsError || authError}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleCredentialsLogin} className="space-y-4 text-left">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5 uppercase tracking-wider">
                  Usuario o Correo
                </label>
                <div className="relative">
                  <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                  <input
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-background-200 bg-background-50/50 text-sm focus:outline-none focus:border-primary-500 focus:bg-white transition-all"
                    placeholder="ejemplo@bidgroup.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5 uppercase tracking-wider">
                  Contraseña
                </label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-background-200 bg-background-50/50 text-sm focus:outline-none focus:border-primary-500 focus:bg-white transition-all"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loadingCredentials}
                className="w-full py-3.5 rounded-xl bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 transition-all cursor-pointer shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {loadingCredentials ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <i className="ri-login-box-line text-base"></i>
                    Ingresar
                  </>
                )}
              </button>
            </form>

            <div className="relative my-6 text-center">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-background-200"></div>
              </div>
              <span className="relative px-3 bg-white text-xs text-foreground-400 uppercase tracking-wider font-medium">
                o continuar con
              </span>
            </div>
            
            <button
              onClick={login}
              className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl border border-background-200 bg-white text-foreground-800 text-xs font-semibold hover:bg-background-50 transition-all cursor-pointer shadow-sm hover:shadow active:scale-[0.98]"
            >
              <i className="ri-microsoft-fill text-lg text-primary-500"></i>
              Iniciar sesión con Microsoft
            </button>
            
            <p className="text-[11px] text-foreground-400 mt-6 leading-relaxed">
              Mesa de Ayuda BID Group protege tus datos mediante encriptación y conexiones seguras.
            </p>
          </div>
        </section>
        <BIDFooter />
      </main>
    );
  }

  // ──────────────────────────────────────────
  // MAIN AUTHENTICATED MICROSITE VIEW
  // ──────────────────────────────────────────

  return (
    <main className="relative min-h-screen bg-background-50">
      <BIDNavbar />

      {/* Hero */}
      <section className="relative flex h-[520px] w-full items-center justify-center overflow-hidden md:h-[620px]">
        <img
          src={getMicrositeBannerImage(currentSlug)}
          alt={deptTitle}
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-white/80 text-xs font-medium mb-5">
              <i className={(staticDeptInfo?.icon || "ri-organization-chart") + " text-sm"}></i>
              {staticDeptInfo?.shortName || "Servicios"} — BID Group
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-3 leading-tight">
              {deptTitle}
            </h1>
            <p className="text-white/65 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
              {deptDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Search & Frequent Tickets */}
      <section className="w-full py-8 px-4 md:px-6 lg:px-10 bg-background-50 border-b border-background-200/50">
        <div className="max-w-4xl mx-auto">
          {/* Search bar */}
          <div className="relative mb-6">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <i className="ri-search-line text-foreground-400 text-lg"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-background-200 bg-background-50/50 text-sm focus:outline-none focus:border-primary-500 focus:bg-white transition-all shadow-sm"
              placeholder={`Escribe lo que necesitas en ${deptTitle} (ej. laptop, correo, acceso)...`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-circle-fill text-lg"></i>
              </button>
            )}
          </div>



          {/* Frequent Tickets / Accesos Rápidos */}
          {!searchQuery && frequentTickets.length > 0 && (
            <div className="mb-6">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400 mb-3.5 flex items-center gap-1.5">
                <i className="ri-star-line text-amber-500"></i>
                Accesos Rápidos / Reportes Frecuentes
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
                {frequentTickets.slice(0, 5).map((t) => (
                  <button
                    key={t.groupName}
                    onClick={() => {
                      setModalService(t.label);
                      setModalGroup(t.groupName);
                      setModalOpen(true);
                    }}
                    className="group p-4 rounded-xl bg-white border border-background-200 hover:border-primary-200 hover:bg-primary-50/10 text-left transition-all cursor-pointer shadow-sm"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center mb-2.5 group-hover:bg-primary-100 transition-colors">
                      <i className="ri-flashlight-line text-sm"></i>
                    </div>
                    <p className="text-xs font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-1">
                      {t.label}
                    </p>
                    <p className="text-[9px] text-foreground-400 mt-0.5 truncate">
                      {t.parentPath}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Main Services catalog (Dynamic Trees) */}
      <section className="w-full py-12 px-4 md:px-6 lg:px-10 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
              Catálogo de Servicios Disponibles
            </h2>
            <p className="text-xs text-foreground-500">
              Navega por la estructura de soporte y selecciona el servicio que necesitas.
            </p>
          </div>

          {(loadingGroups || permittedGroupIds === null) && zammadGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-8 h-8 border-3 border-primary-500/20 border-t-primary-500 rounded-full animate-spin mb-3"></div>
              <p className="text-xs text-foreground-500">Cargando datos...</p>
            </div>
          ) : groupsError ? (
            <div className="p-5 rounded-2xl bg-red-50 border border-red-100 text-center py-10 max-w-md mx-auto">
              <i className="ri-error-warning-line text-red-500 text-3xl mb-3 block"></i>
              <p className="text-sm font-semibold text-red-800 mb-1">Error al consultar catálogo</p>
              <p className="text-xs text-red-600">{groupsError}</p>
            </div>
          ) : !hasSearchResults ? (
            <div className="p-8 rounded-2xl bg-white border border-background-200/80 text-center py-16 max-w-md mx-auto shadow-sm">
              <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-3 text-primary-600">
                <i className="ri-search-line text-xl"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-800 mb-1">Sin resultados</p>
              <p className="text-xs text-foreground-500 max-w-xs mx-auto leading-relaxed">
                No encontramos ningún servicio que coincida con "{searchQuery}". Intenta con otros términos.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              {(() => {
                const allSystems: { parentName: string; system: ZammadGroupNode }[] = [];

                for (const tree of parsedTrees) {
                  if (tree.children.length === 0) {
                    // Root group itself has no children (flat parent group)
                    allSystems.push({
                      parentName: tree.parentName,
                      system: {
                        id: -1,
                        name: tree.parentName,
                        label: tree.parentName,
                        children: [],
                      },
                    });
                  } else {
                    for (const system of tree.children) {
                      allSystems.push({ parentName: tree.parentName, system });
                    }
                  }
                }

                return allSystems.map(({ parentName, system }) => {
                  // Default behavior: collapsed unless explicitly expanded via toggle
                  const isExpanded = collapsedSystems.has(system.name);
                  
                  // Count total leaves under system
                  let leafCount = 0;
                  function countLeaves(n: ZammadGroupNode) {
                    if (n.children.length === 0) leafCount++;
                    else n.children.forEach(countLeaves);
                  }
                  countLeaves(system);

                  // If system has no children at all (flat single node leaf), render direct ticket card
                  if (system.children.length === 0) {
                    const matchesSearch = !searchQuery.trim() ||
                      system.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      system.name.toLowerCase().includes(searchQuery.toLowerCase());

                    if (!matchesSearch) return null;

                    return (
                      <div key={system.name} className="rounded-xl bg-white border border-background-200/80 shadow-sm overflow-hidden">
                        <button
                          onClick={() => openTicketModal(system)}
                          className="w-full flex items-center gap-3 p-4 cursor-pointer hover:bg-accent-50/30 transition-colors text-left group/sys"
                        >
                          <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 group-hover/sys:bg-accent-200 transition-colors">
                            <i className={`${getServiceIcon(system.label)} text-accent-600 text-base`}></i>
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-[13px] font-heading font-semibold text-foreground-900">{system.label}</h3>
                            <p className="text-[11px] text-foreground-500">{parentName}</p>
                          </div>
                          <i className="ri-arrow-right-s-line text-foreground-400 text-lg group-hover/sys:text-accent-600 transition-colors"></i>
                        </button>
                      </div>
                    );
                  }

                  // Check if any leaf under system matches search query
                  let hasMatchingLeaf = false;
                  function checkMatching(n: ZammadGroupNode) {
                    if (n.children.length === 0) {
                      if (!searchQuery.trim() ||
                          n.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          n.name.toLowerCase().includes(searchQuery.toLowerCase())) {
                        hasMatchingLeaf = true;
                      }
                    } else {
                      n.children.forEach(checkMatching);
                    }
                  }
                  checkMatching(system);

                  if (!hasMatchingLeaf) return null;

                  return (
                    <div key={system.name} className="rounded-xl bg-white border border-background-200/80 shadow-sm overflow-hidden">
                      {/* System header — clickable to toggle expand/collapse */}
                      <button
                        onClick={() => toggleSystemCollapse(system.name)}
                        className="w-full flex items-center gap-3 p-4 cursor-pointer hover:bg-accent-50/30 transition-colors text-left group/sys"
                      >
                        <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 group-hover/sys:bg-accent-200 transition-colors">
                          <i className={`${getServiceIcon(system.label)} text-accent-600 text-base`}></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-[13px] font-heading font-semibold text-foreground-900">{system.label}</h3>
                          <p className="text-[11px] text-foreground-500">{system.children.length} categorías · {leafCount} solicitudes</p>
                        </div>
                        <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}>
                          <i className="ri-arrow-down-s-line text-foreground-400 text-sm"></i>
                        </div>
                      </button>

                      {/* Categories / Sub-nodes collapsible */}
                      <div
                        className={`transition-all duration-300 overflow-hidden ${
                          isExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="border-t border-background-200/60 p-2">
                          {(() => {
                            const renderNode = (node: ZammadGroupNode, depth: number = 0): React.ReactNode => {
                              // If node has no children (Leaf Node), render actionable ticket button
                              if (node.children.length === 0) {
                                const matchesSearch =
                                  !searchQuery.trim() ||
                                  node.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                  node.name.toLowerCase().includes(searchQuery.toLowerCase());
                                if (!matchesSearch) return null;

                                return (
                                  <button
                                    key={node.name}
                                    onClick={() => openTicketModal(node)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-lg cursor-pointer hover:bg-accent-50/70 transition-all text-left group/leaf border border-transparent hover:border-accent-200/60"
                                    style={{ paddingLeft: `${Math.max(12, depth * 16 + 12)}px` }}
                                  >
                                    <div className="w-6 h-6 rounded-md bg-accent-100/80 flex items-center justify-center flex-shrink-0 group-hover/leaf:bg-accent-200 group-hover/leaf:scale-105 transition-all">
                                      <i className={`${getServiceIcon(node.label)} text-accent-600 text-xs`}></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-foreground-800 group-hover/leaf:text-accent-700 transition-colors line-clamp-1">
                                        {node.label}
                                      </p>
                                    </div>
                                    <i className="ri-arrow-right-s-line text-foreground-400 text-sm group-hover/leaf:text-accent-600 group-hover/leaf:translate-x-0.5 transition-all"></i>
                                  </button>
                                );
                              }

                              // If node has children (Branch Node), render collapsible container
                              const isSubExpanded = expandedKey === node.name || collapsedSystems.has(node.name);

                              return (
                                <div key={node.name} className="my-1 rounded-lg border border-background-200/50 overflow-hidden bg-background-50/40">
                                  <button
                                    onClick={() => {
                                      toggleCategory(node.name);
                                      toggleSystemCollapse(node.name);
                                    }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left cursor-pointer hover:bg-accent-50/40 transition-colors group/branch"
                                    style={{ paddingLeft: `${Math.max(12, depth * 16 + 12)}px` }}
                                  >
                                    <div className="w-6 h-6 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0 group-hover/branch:bg-accent-200 transition-colors">
                                      <i className={`${getServiceIcon(node.label)} text-accent-600 text-xs`}></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-foreground-900 group-hover/branch:text-accent-700 transition-colors truncate">
                                        {node.label}
                                      </p>
                                    </div>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-100/60 text-accent-700 font-medium">
                                      {node.children.length}
                                    </span>
                                    <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${isSubExpanded ? "rotate-180" : ""}`}>
                                      <i className="ri-arrow-down-s-line text-foreground-400 text-xs"></i>
                                    </div>
                                  </button>

                                  <div className={`transition-all duration-300 overflow-hidden ${isSubExpanded ? "max-h-[3000px] opacity-100" : "max-h-0 opacity-0"}`}>
                                    <div className="p-1 space-y-0.5 bg-white">
                                      {node.children.map((child) => renderNode(child, depth + 1))}
                                    </div>
                                  </div>
                                </div>
                              );
                            };

                            return system.children.map((childNode) => renderNode(childNode, 0));
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </section>

      {/* Dynamic CTA for general request in root group */}
      {/* Dynamic CTA for general request in root group */}
      {hasParentPermission && (
        <section className="w-full py-12 px-4 md:px-6 lg:px-10 bg-background-100/60">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-1">
              ¿No encuentras la sección correcta?
            </h2>
            <p className="text-xs text-foreground-500 mb-5">
              Si tu necesidad no está listada, envía una solicitud al buzón principal del departamento.
            </p>
            <button
              onClick={() => {
                setModalService("Solicitud General");
                setModalGroup(targetParentCategoryNodes[0]?.name || "General");
                setModalOpen(true);
              }}
              className="px-8 py-3.5 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all flex items-center gap-2 mx-auto"
            >
              <i className="ri-add-circle-line text-lg"></i>
              Nueva Solicitud General
            </button>
          </div>
        </section>
      )}

      <BIDFooter />

      <ServiceRequestModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        serviceName={modalService}
        groupName={modalGroup}
        formUrl=""
        departmentName={deptTitle}
      />
    </main>
  );
}
